import React, { useState } from "react";
import { usePageMeta } from '../hooks/usePageMeta';
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  LineaCheck,
  LineaArrowLeft,
  LineaArrowRight,
} from "../components/LineaIcons";
import { getTranslation } from "../components/translations";
import { useToast } from "@/components/ui/use-toast";

const LOGO_URL = "/v4logo.png";

export default function Pricing() {
  const t = (key) => getTranslation(key);
  const navigate = useNavigate();
  const { toast } = useToast();

  usePageMeta({
    title: 'V4LLUED Guns Pricing | Fitness Gaming Plans | Active Gaming',
    description: 'Choose your Active Gaming plan. Flexible pricing for V4LLUED Guns Joy-Con controllers — start your fitness gaming journey with a free trial. No commitment required.',
    ogImage: `${window.location.origin}/v4logo.png`,
    ogUrl: window.location.origin + '/Pricing',
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      try {
        const userData = JSON.parse(atob(ref));
        localStorage.setItem("vsteps_user", JSON.stringify(userData));
      } catch (e) {
        console.error("Failed to decode VSteps ref:", e);
      }
    }
  }, []);

  const features = [t("feature0"), t("feature1"), t("feature3"), t("feature4")];

  const freePlan = {
    id: "free",
    name: "3 DAYS FREE",
    price: "FREE",
    period: "",
    description:
      "Try VSteps free for 3 days. No payment required to get started.",
    features: features.filter((f) => f !== t("feature3")),
    buttonStyle: "free",
  };

  const plans = [
    {
      id: "monthly",
      name: t("monthlyMembership"),
      price: "$2.99",
      period: t("perMonth"),
      description: t("monthlyDesc"),
      features: features,
      buttonStyle: "outline",
    },
    {
      id: "yearly",
      name: t("yearlyMembership"),
      price: "$24.99",
      period: t("perYear"),
      description: t("yearlyDesc"),
      features: features,
      buttonStyle: "outline",
    },
    {
      id: "lifetime",
      name: t("lifetimeMembership"),
      price: "$39.99",
      period: t("oneTime"),
      description: t("lifetimeDesc"),
      features: features,
      buttonStyle: "solid",
      badge: t("bestValue"),
    },
  ];

  const handleSelectPlan = () => {
    toast({
      title: "Sign In Required",
      description: "You must be signed in to purchase a subscription.",
      variant: "destructive",
    });
  };

  const allPlans = [freePlan, ...plans];

  const handleSwipe = (direction) => {
    if (direction === "left" && currentIndex < allPlans.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (direction === "right" && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] text-white font-sans">
      {/* Header */}
      <header>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="VSteps" className="h-8 sm:h-10 w-auto" />
          </div>

          <Button
            onClick={() => navigate("/steps")}
            variant="outline"
            className="border-[#333] text-white hover:bg-white/5 bg-transparent"
          >
            {t("backToHome")}
          </Button>
        </nav>
      </header>

      {/* Pricing Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-xl sm:text-2xl md:text-3xl font-normal text-white mb-4">
            {t("selectPlan")}
          </h1>
        </motion.div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-4 gap-8">
          {[freePlan, ...plans].map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{
                boxShadow:
                  "inset 0 0 60px #4b0000, inset 20px 0 80px #990000, 0 0 50px #ff0000",
                transition: { duration: 0.3 },
              }}
              className="relative bg-[#161616] border-2 border-[#333] p-8 hover:border-[#FF0000] transition-all duration-300 group rounded-lg"
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FF0000] px-4 py-1 text-xs font-bold">
                  {plan.badge}
                </div>
              )}

              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-normal text-[#FF0000] mb-2">
                  {plan.name}
                </h2>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl sm:text-5xl font-normal text-white">
                    {plan.price}
                  </span>
                  <span className="text-[#C5C6C7] text-sm">{plan.period}</span>
                </div>
                <p className="text-[#C5C6C7] text-sm leading-relaxed">
                  {plan.description}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <LineaCheck className="w-5 h-5 text-[#FF0000] flex-shrink-0 mt-0.5" />
                    <span className="text-white text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {plan.buttonStyle === "free" ? (
                <a
                  href="https://vsteps.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <Button className="w-full font-bold tracking-wider bg-transparent border-2 border-white/30 text-white hover:bg-white/10 group-hover:animate-pulse">
                    Initialize
                  </Button>
                </a>
              ) : (
                <Button
                  onClick={handleSelectPlan}
                  data-testid={`button-select-${plan.id}`}
                  className={`w-full font-bold tracking-wider ${
                    plan.buttonStyle === "solid"
                      ? "bg-[#FF0000] hover:bg-[#FF0000]/90 text-white"
                      : "bg-transparent border-2 border-[#FF0000] text-[#FF0000] hover:bg-[#FF0000]/10"
                  } group-hover:animate-pulse`}
                >
                  {t("initiate")}
                </Button>
              )}
            </motion.div>
          ))}
        </div>

        {/* Mobile Swiper */}
        <div className="md:hidden relative">
          <div className="relative overflow-hidden px-4 pt-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={`mobile-${currentIndex}`}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.3 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(e, { offset, velocity }) => {
                  const swipe = Math.abs(offset.x) * velocity.x;
                  if (swipe < -500) {
                    handleSwipe("left");
                  } else if (swipe > 500) {
                    handleSwipe("right");
                  }
                }}
                className="relative bg-[#161616] border-2 border-[#FF0000] p-6 shadow-[0_0_50px_rgba(255,0,0,0.5)] rounded-lg"
              >
                {allPlans[currentIndex].badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FF0000] px-4 py-1 text-xs font-bold">
                    {allPlans[currentIndex].badge}
                  </div>
                )}

                <div className="mb-6">
                  <h2 className="text-2xl font-normal text-[#FF0000] mb-2">
                    {allPlans[currentIndex].name}
                  </h2>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-4xl font-normal text-white">
                      {allPlans[currentIndex].price}
                    </span>
                    <span className="text-[#C5C6C7] text-sm">
                      {allPlans[currentIndex].period}
                    </span>
                  </div>
                  <p className="text-[#C5C6C7] text-sm leading-relaxed">
                    {allPlans[currentIndex].description}
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {allPlans[currentIndex].features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <LineaCheck className="w-5 h-5 text-[#FF0000] flex-shrink-0 mt-0.5" />
                      <span className="text-white text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {allPlans[currentIndex].buttonStyle === "free" ? (
                  <a
                    href="https://vsteps.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <Button className="w-full font-bold tracking-wider bg-transparent border-2 border-white/30 text-white hover:bg-white/10">
                      Initialize
                    </Button>
                  </a>
                ) : (
                  <Button
                    onClick={handleSelectPlan}
                    data-testid={`button-select-mobile-${allPlans[currentIndex].id}`}
                    className="w-full font-bold tracking-wider bg-[#FF0000] hover:bg-[#FF0000]/90 text-white"
                  >
                    {t("initiate")}
                  </Button>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => handleSwipe("right")}
            disabled={currentIndex === 0}
            className="absolute -left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#FF0000] disabled:bg-[#FF0000]/30 disabled:cursor-not-allowed rounded-full flex items-center justify-center shadow-lg z-10"
          >
            <LineaArrowLeft className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={() => handleSwipe("left")}
            disabled={currentIndex === allPlans.length - 1}
            className="absolute -right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#FF0000] disabled:bg-[#FF0000]/30 disabled:cursor-not-allowed rounded-full flex items-center justify-center shadow-lg z-10"
          >
            <LineaArrowRight className="w-4 h-4 text-white" />
          </button>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {allPlans.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-[#FF0000] w-8"
                    : "bg-[#C5C6C7]/30"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="text-[#C5C6C7] text-sm">{t("securityInfo")}</p>
        </motion.div>
      </div>
    </div>
  );
}
